import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { uxListsApi } from "./api";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast, useToast } from "@shared/hooks/use-toast";
import {
  useRemoteUtilityId,
  useUserId,
} from "@shared/selectors/globalSelectors";
import { ListFilters, ListOwnership } from "./types";
import { transformToRecipientListPayload } from "./utils";
import { downloadCSV, downloadFile } from "@shared/utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

// Add your feature-specific hooks here

export const usePostRecipientList = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => uxListsApi.postRecipientList(payload),
    {
      onSuccess: (data) => {
        toast({
          title: "Success!",
          description: "Recipient list created successfully",
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message || "Failed to create recipient list",
          variant: "destructive",
        });
      },
    }
  );
};

export const useConfigCategoryJson = (
  config: string,
  remoteUtilityId: string,
  configLevel: string,
  codeList?: any,
  options?: any
) =>
  useSmartQuery(
    QueryKeyFactory.module.ux.lists.configCategoryJson(
      config,
      remoteUtilityId,
      configLevel,
      codeList
    ),
    () =>
      uxListsApi.fetchConfigCategoryJson(
        config,
        remoteUtilityId,
        configLevel,
        codeList
      ),
    {
      ...options,
    }
  );

export const useFetchConsumerData = (
  remoteUtilityId: string,
  currentPage,
  limit,
  filterType: "CATEGORY" | "TERRITORY",
  categoryTypeOrTerritoryType: string,
  codeList: string[] = [],
  options?: any
) => {
  const uniqueKey = codeList.join(",");

  return useSmartQuery(
    QueryKeyFactory.module.ux.lists.fetchConsumerData(
      remoteUtilityId,
      currentPage,
      filterType,
      uniqueKey
    ),
    () =>
      uxListsApi.fetchConsumerData({
        remoteUtilityId,
        page: currentPage,
        limit,
        filterType,
        categoryType:
          filterType === "CATEGORY" ? categoryTypeOrTerritoryType : undefined,
        categoryCodes: filterType === "CATEGORY" ? codeList : undefined,
        territoryType:
          filterType === "TERRITORY" ? categoryTypeOrTerritoryType : undefined,
        territoryCodes: filterType === "TERRITORY" ? codeList : undefined,
      }),
    {
      enabled: !!remoteUtilityId,
      ...options,
    }
  );
};

export const usePatchRecipientList = (id: string | number, options?: any) => {
  return useSmartMutation(
    (payload: any) => uxListsApi.patchRecipientList(id, payload),
    {
      ...options,
    }
  );
};

export const useRecipientListDetail = (
  remoteUtilityId: string | number,
  listId: string | number,
  isEdit?: boolean
) =>
  useSmartQuery(
    QueryKeyFactory.module.ux.lists.recipientListDetail(listId),
    () => uxListsApi.fetchRecipientListDetail(remoteUtilityId, listId),
    {
      enabled: !!remoteUtilityId && isEdit && !!listId,
    }
  );

export const useRecipientLists = (filters: ListFilters) => {
  const utilityId = useRemoteUtilityId();
  const userId = useUserId();

  // Prepare API filters
  const apiFilters = {
    // Only include recipientType if it's not "all" or undefined
    ...(filters.recipientType &&
      filters.recipientType !== "all" && {
        recipientType: filters.recipientType,
      }),
    filterType: filters.filterType,
    isActive: filters.isActive,
    search_data: filters.search_data,
    page: filters.page,
    limit: filters.limit,
    userId: filters.listOwnership === "my-lists" ? userId : undefined,
  };

  return useSmartQuery(
    [
      QueryKeyFactory.module.communication.recipientList(),
      "list",
      utilityId,
      apiFilters, // Include all filters in cache key for proper caching
    ],
    async () => {
      if (filters.listOwnership === "my-lists" && !userId)
        throw new Error("User ID is required for 'my-lists'");

      return await uxListsApi.getRecipientLists(utilityId, apiFilters);
    },
    {
      enabled: !!utilityId,
    }
  );
};

export const useCommunicationListsExport = (
  utilityId: string,
  filters: {
    recipientType?: string;
    filterType?: string;
    isActive?: boolean[];
    search_data?: string;
    userId?: string;
  }
) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      const blob = await uxListsApi.exportRecipientLists(utilityId, {
        ...filters,
        // Remove pagination for export to get all data
        page: undefined,
        limit: undefined,
      });
      toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
    } catch (error: any) {
      toast({
        title: `Failed to export communication lists: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [utilityId, filters, isExporting]);

  return { exportCSV, isExporting };
};

// Hook for creating recipient lists
export const useCreateRecipientList = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartMutation(
    async (formData: {
      name: string;
      recipient_type: "CONSUMER" | "TECHNICIAN" | "BUSINESS";
      description: string;
      tags: string[];
    }) => {
      // Transform form data to API format
      const apiPayload = transformToRecipientListPayload(formData, utilityId);

      // Call API
      return uxListsApi.createRecipientList(apiPayload);
    },
    {
      invalidateKeys: [
        // Invalidate recipient lists for all types and ownership combinations
        [
          QueryKeyFactory.module.communication.recipientList(),
          ,
          utilityId,
          "CONSUMER",
          "my-lists",
        ],
        [
          QueryKeyFactory.module.communication.recipientList(),
          ,
          utilityId,
          "CONSUMER",
          "all",
        ],
        [
          QueryKeyFactory.module.communication.recipientList(),
          ,
          utilityId,
          "TECHNICIAN",
          "my-lists",
        ],
        [
          QueryKeyFactory.module.communication.recipientList(),
          ,
          utilityId,
          "TECHNICIAN",
          "all",
        ],
        [
          QueryKeyFactory.module.communication.recipientList(),
          ,
          utilityId,
          "BUSINESS",
          "my-lists",
        ],
        [
          QueryKeyFactory.module.communication.recipientList(),
          ,
          utilityId,
          "BUSINESS",
          "all",
        ],
        // Or use QueryKeyFactory if you have one for recipient lists
      ],
      onSuccess: (createdList, variables) => {
        toast({ title: `"${variables.name}" has been created successfully!` });
      },
      onError: (error: any) => {
        toast(error?.message || "Failed to create recipient list");
      },
    }
  );
};
export const useToggleListStatus = () => {
  const queryClient = useQueryClient();
  return useSmartMutation(
    async (data: { listId: number; isActive: boolean }) => {
      if (!data.listId) {
        throw new Error("List ID is missing");
      }

      return uxListsApi.toggleListStatus(data.listId, data.isActive);
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [["communicationRecipientList", "list"]],
        });
      },
      onError: (error: any) => {},
    }
  );
};
export const useDownloadCSV = () => {
  return useSmartMutation(
    async ({
      taskID,
      filename = "contact_list.csv",
    }: {
      taskID: number;
      filename?: string;
    }) => {
      const blob = await uxListsApi.downloadContactList(taskID);
      toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
    }
  );
};
